/**
 * Home Page Component - Daily Tasks / Company Queue
 *
 * Refactored: Components and hooks extracted to separate modules
 *
 * Scroll preservation: when navigating to a company detail and back, we
 * restore the scroll position the user had when they left the page. This
 * pairs with URL-backed filters so the EXACT prior view is reproduced.
 */
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { X, Target } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { ResponsiveLayout } from "../../components/layout";
import { targetListsApi } from "../TargetLists/api";

// Import components
import {
  StatsCounter,
  DesktopStatsGrid,
  SalesFunnel,
  CompanyList,
  SearchInput,
  FilterToggle,
  DesktopFilterPanel,
  MobileFilterPanel,
  QueueTabs,
  AddCompanyButton,
  PointsCardDesktop,
  PointsCardMobile,
  TopAgentsCard
} from "./components";

// Import hooks
import { useHomePageData } from "./hooks";

const SCROLL_STORAGE_KEY = "dnevni_zadaci_scroll";

const HomePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  
  // Get all data and state from custom hook
  const {
    filteredCompanies,
    displayedCompanies,
    loading,
    activeTab,
    setActiveTab,
    stats,
    statsLoading,
    scores,
    scoresLoading,
    topAgents,
    topAgentsLoading,
    funnelData,
    funnelLoading,
    funnelStatusFilter,
    handleFunnelStatusClick,
    // Server-side pagination
    currentPage,
    totalCount,
    totalPages,
    tabCounts,
    hasMore,
    remainingCount,
    handleLoadMore,
    searchQuery,
    setSearchQuery,
    cityFilter,
    setCityFilter,
    industryFilter,
    setIndustryFilter,
    nextActionFilter,
    setNextActionFilter,
    agentFilter,
    setAgentFilter,
    agents,
    sourceFilter,
    setSourceFilter,
    sources,
    targetListId,
    setTargetListId,
    targetLists,
    showFilters,
    setShowFilters,
    uniqueCities,
    uniqueIndustries,
    hasActiveFilters,
    activeFilterCount,
    clearAllFilters,
    resetDisplayCount,
    isTeamLeader,
    canFilterByAgent
  } = useHomePageData(token, user);

  // Scroll preservation: save Y on click + restore Y after the company list
  // re-hydrates from the same URL. Key includes the full querystring so
  // different filter sets keep separate scroll memories.
  const scrollContainerRef = useRef(null);
  const restoredScrollKeyRef = useRef(null);
  const getScrollKey = () => `${location.pathname}${location.search}`;

  const getScrollContainer = () => {
    // The mobile/desktop layouts use different scroll containers. Use
    // document scrolling element as fallback.
    return scrollContainerRef.current
      || document.scrollingElement
      || document.documentElement;
  };

  const handleCompanyClick = (companyId) => {
    try {
      const el = getScrollContainer();
      const y = el?.scrollTop || window.scrollY || 0;
      sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify({
        key: getScrollKey(),
        y,
        savedAt: Date.now(),
      }));
    } catch { /* ignore quota or JSON errors — scroll preservation is best-effort */ }
    navigate(`/company/${companyId}`);
  };

  const handleAddCompany = () => navigate("/company/new");
  const handleFilterChange = () => resetDisplayCount();
  const handleSearchChange = (value) => { setSearchQuery(value); resetDisplayCount(); };

  // Restore scroll once after the data finishes loading for the matching URL.
  useEffect(() => {
    if (loading) return;
    const key = getScrollKey();
    if (restoredScrollKeyRef.current === key) return;
    try {
      const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.key !== key) return;
      // 60s freshness window — stale entries are ignored.
      if (Date.now() - (parsed.savedAt || 0) > 60_000) return;
      const el = getScrollContainer();
      if (el && typeof parsed.y === "number") {
        // Defer to next frame so the list has been painted.
        requestAnimationFrame(() => { el.scrollTop = parsed.y; });
      }
      restoredScrollKeyRef.current = key;
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    } catch { /* ignore */ }
  }, [loading, location.search]);

  // Resolve target list name for the banner
  const [activeTargetListName, setActiveTargetListName] = useState(null);
  useEffect(() => {
    if (!targetListId) {
      setActiveTargetListName(null);
      return;
    }
    targetListsApi.get(targetListId)
      .then(r => setActiveTargetListName(r.data?.name || null))
      .catch(() => setActiveTargetListName(null));
  }, [targetListId]);

  // Determine if points card should be shown (not for manager/admin)
  const showPointsCard = user?.role !== 'manager' && user?.role !== 'admin';

  return (
    <ResponsiveLayout title="Dnevni zadaci" showLogout>
      {/* Active target list banner */}
      {targetListId && (
        <div
          data-testid="active-target-list-banner"
          className="mb-3 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2"
        >
          <div className="flex items-center gap-2 text-sm">
            <Target size={16} />
            Prikazujem samo firme iz liste:&nbsp;
            <button
              onClick={() => navigate(`/target-lists/${targetListId}`)}
              className="font-medium underline hover:no-underline"
            >
              {activeTargetListName || "..."}
            </button>
          </div>
          <button
            onClick={() => setTargetListId("")}
            className="text-amber-900 hover:text-amber-700"
            data-testid="active-target-list-clear"
            title="Ukloni filter"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {isDesktop ? (
        /* ==================== DESKTOP LAYOUT ==================== */
        <>
          {/* Stats Scope Indicator */}
          {(user?.role === 'team_leader' || user?.role === 'manager' || user?.role === 'admin') && (
            <div className="text-sm text-gray-500 mb-2 font-medium">
              Statistika ovaj mjesec {user?.role === 'team_leader' ? '(Tim)' : '(Svi korisnici)'}
            </div>
          )}
          
          {/* Stats Grid */}
          <DesktopStatsGrid stats={stats} loading={statsLoading} />

          {/* Sales Funnel */}
          <SalesFunnel 
            funnelData={funnelData} 
            loading={funnelLoading} 
            onStatusClick={handleFunnelStatusClick}
            activeStatus={funnelStatusFilter}
          />

          {/* Two Column Layout */}
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Company List */}
            <div className="col-span-2">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4">
                {/* Header with Tabs and Add Button */}
                <div className="flex items-center justify-between mb-4">
                  <QueueTabs activeTab={activeTab} onTabChange={setActiveTab} />
                  <AddCompanyButton onClick={handleAddCompany} />
                </div>
                
                {/* Search */}
                <SearchInput 
                  value={searchQuery} 
                  onChange={handleSearchChange}
                  onClear={() => setSearchQuery("")}
                  testId="company-search-input"
                />

                {/* Filter Toggle */}
                <FilterToggle 
                  showFilters={showFilters}
                  hasActiveFilters={hasActiveFilters}
                  activeCount={activeFilterCount}
                  onToggle={() => setShowFilters(!showFilters)}
                  onClear={clearAllFilters}
                />

                {/* Filters Panel */}
                {showFilters && (
                  <DesktopFilterPanel
                    cityFilter={cityFilter}
                    setCityFilter={setCityFilter}
                    industryFilter={industryFilter}
                    setIndustryFilter={setIndustryFilter}
                    nextActionFilter={nextActionFilter}
                    setNextActionFilter={setNextActionFilter}
                    agentFilter={agentFilter}
                    setAgentFilter={setAgentFilter}
                    sourceFilter={sourceFilter}
                    setSourceFilter={setSourceFilter}
                    targetListId={targetListId}
                    setTargetListId={setTargetListId}
                    uniqueCities={uniqueCities}
                    uniqueIndustries={uniqueIndustries}
                    agents={agents}
                    sources={sources}
                    targetLists={targetLists}
                    canFilterByAgent={canFilterByAgent}
                    isTeamLeader={isTeamLeader}
                    onFilterChange={handleFilterChange}
                  />
                )}
              </div>

              {/* Company List */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                <div className="xl:col-span-2">
                  <CompanyList
                    companies={filteredCompanies}
                    totalCount={totalCount}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    loading={loading}
                    searchQuery={searchQuery}
                    activeTab={activeTab}
                    hasMore={hasMore}
                    remainingCount={remainingCount}
                    onCompanyClick={handleCompanyClick}
                    onLoadMore={handleLoadMore}
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Points & Top Agents */}
            <div className="col-span-1">
              {showPointsCard && (
                <PointsCardDesktop scores={scores} loading={scoresLoading} />
              )}
              <div className={showPointsCard ? "mt-4" : ""}>
                <TopAgentsCard agents={topAgents} loading={topAgentsLoading} />
              </div>
            </div>
          </div>
        </>
      ) : (
        /* ==================== MOBILE LAYOUT ==================== */
        <>
          {/* Stats Counter */}
          <StatsCounter stats={stats} loading={statsLoading} userRole={user?.role} />

          {/* Sales Funnel */}
          <SalesFunnel 
            funnelData={funnelData} 
            loading={funnelLoading} 
            onStatusClick={handleFunnelStatusClick}
            activeStatus={funnelStatusFilter}
          />

          {/* Points Card */}
          {showPointsCard && (
            <PointsCardMobile scores={scores} loading={scoresLoading} />
          )}

          {/* Top Agents */}
          <TopAgentsCard agents={topAgents} loading={topAgentsLoading} />

          {/* Tabs & Add Button */}
          <div className="flex items-center justify-between mb-3">
            <QueueTabs activeTab={activeTab} onTabChange={setActiveTab} isMobile />
            <AddCompanyButton onClick={handleAddCompany} isMobile />
          </div>

          {/* Search */}
          <div className="mb-4">
            <SearchInput 
              value={searchQuery} 
              onChange={handleSearchChange}
              onClear={() => setSearchQuery("")}
              placeholder="Pretraži po nazivu ili ID..."
              testId="company-search-input-mobile"
            />
          </div>

          {/* Filter Toggle */}
          <div className="mb-3">
            <FilterToggle 
              showFilters={showFilters}
              hasActiveFilters={hasActiveFilters}
              activeCount={activeFilterCount}
              onToggle={() => setShowFilters(!showFilters)}
              onClear={clearAllFilters}
            />
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <MobileFilterPanel
              cityFilter={cityFilter}
              setCityFilter={setCityFilter}
              industryFilter={industryFilter}
              setIndustryFilter={setIndustryFilter}
              nextActionFilter={nextActionFilter}
              setNextActionFilter={setNextActionFilter}
              agentFilter={agentFilter}
              setAgentFilter={setAgentFilter}
              sourceFilter={sourceFilter}
              setSourceFilter={setSourceFilter}
              targetListId={targetListId}
              setTargetListId={setTargetListId}
              uniqueCities={uniqueCities}
              uniqueIndustries={uniqueIndustries}
              agents={agents}
              sources={sources}
              targetLists={targetLists}
              canFilterByAgent={canFilterByAgent}
              isTeamLeader={isTeamLeader}
              onFilterChange={handleFilterChange}
            />
          )}

          {/* Company List */}
          <CompanyList
            companies={filteredCompanies}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            searchQuery={searchQuery}
            activeTab={activeTab}
            hasMore={hasMore}
            remainingCount={remainingCount}
            onCompanyClick={handleCompanyClick}
            onLoadMore={handleLoadMore}
          />
        </>
      )}
    </ResponsiveLayout>
  );
};

export default HomePage;
